/**
 * ===========================================
 * StudySpace ERP Frontend - Cafe API Service
 * ===========================================
 * 
 * Provides API functions for cafe item management and sales.
 * Uses Axios instance with auth tokens and error handling.
 * 
 * Features:
 * - Cafe item CRUD operations
 * - Item restock functionality
 * - Low stock item queries
 * - Cafe sales creation and listing
 * - Cafe payment recording
 * 
 * IMPORTANT (NON-NEGOTIABLE):
 * This system only supports CASH payments.
 * 
 * @file src/api/cafe.api.js
 * @description Cafe API service
 * @task T115
 */

import api, { handleApiError } from './axios';

// ===========================================
// CAFE ITEM API FUNCTIONS
// ===========================================

/**
 * List cafe items with optional filters
 * 
 * @param {Object} [params={}] - Query parameters
 * @param {string} [params.category] - Filter by category (beverage/snack/meal/other)
 * @param {boolean} [params.isAvailable] - Filter by availability
 * @param {boolean} [params.lowStockOnly] - Show only low stock items
 * @param {string} [params.search] - Search by name
 * @param {number} [params.page] - Page number
 * @param {number} [params.limit] - Page size
 * @param {string} [params.sort] - Sort field:direction (e.g., 'name:asc')
 * @returns {Promise<Object>} API response with items and pagination
 * 
 * @example
 * // Get all beverages
 * const result = await listItems({ category: 'beverage' });
 * 
 * @example
 * // Get low stock items
 * const result = await listItems({ lowStockOnly: true });
 */
export const listItems = async (params = {}) => {
  try {
    const response = await api.get('/cafe/items', { params });
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Get a single cafe item by ID
 * 
 * @param {string} id - Item ID
 * @returns {Promise<Object>} API response with item data
 */
export const getItem = async (id) => {
  try {
    const response = await api.get(`/cafe/items/${id}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Create a new cafe item
 * 
 * @param {Object} data - Item data
 * @param {string} data.name - Item name
 * @param {string} data.category - Category (beverage/snack/meal/other)
 * @param {number} data.price - Selling price
 * @param {number} data.cost - Cost price
 * @param {number} data.quantity - Initial stock quantity
 * @param {number} [data.lowStockThreshold] - Alert threshold (default: 5)
 * @param {string} [data.unit] - Unit of measurement (default: 'pieces')
 * @param {string} [data.description] - Item description
 * @param {string} [data.imageUrl] - Image URL
 * @returns {Promise<Object>} API response with created item
 * 
 * @example
 * const newItem = await createItem({
 *   name: 'Cappuccino',
 *   category: 'beverage',
 *   price: 35,
 *   cost: 15,
 *   quantity: 100
 * });
 */
export const createItem = async (data) => {
  try {
    const response = await api.post('/cafe/items', data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Update an existing cafe item
 * 
 * @param {string} id - Item ID
 * @param {Object} data - Updated item data
 * @returns {Promise<Object>} API response with updated item
 */
export const updateItem = async (id, data) => {
  try {
    const response = await api.patch(`/cafe/items/${id}`, data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Delete (deactivate) a cafe item
 * Note: Items are soft deleted (marked as unavailable)
 * 
 * @param {string} id - Item ID
 * @returns {Promise<Object>} API response
 */
export const deleteItem = async (id) => {
  try {
    const response = await api.delete(`/cafe/items/${id}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Restock a cafe item (add to quantity)
 * 
 * @param {string} id - Item ID
 * @param {Object} data - Restock data
 * @param {number} data.quantity - Quantity to add
 * @param {string} [data.notes] - Optional restock notes
 * @returns {Promise<Object>} API response with updated item
 * 
 * @example
 * await restockItem(itemId, { quantity: 50, notes: 'Weekly restock' });
 */
export const restockItem = async (id, data) => {
  try {
    const response = await api.post(`/cafe/items/${id}/restock`, data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Get all low stock items
 * 
 * @returns {Promise<Object>} API response with low stock items
 */
export const getLowStockItems = async () => {
  try {
    const response = await api.get('/cafe/items/low-stock');
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

// ===========================================
// CAFE SALES API FUNCTIONS
// ===========================================

/**
 * Create a new cafe sale
 * Note: Inventory is automatically decremented on sale creation
 * 
 * @param {Object} data - Sale data
 * @param {Array} data.items - Array of items to sell
 * @param {string} data.items[].itemId - Item ID
 * @param {number} data.items[].quantity - Quantity to sell
 * @param {string} [data.customerId] - Customer user ID (optional)
 * @param {string} [data.notes] - Sale notes
 * @returns {Promise<Object>} API response with created sale
 * 
 * @example
 * const sale = await createSale({
 *   items: [
 *     { itemId: 'item123', quantity: 2 },
 *     { itemId: 'item456', quantity: 1 }
 *   ],
 *   notes: 'Regular order'
 * });
 */
export const createSale = async (data) => {
  try {
    const response = await api.post('/cafe/sales', data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * List cafe sales with optional filters
 * 
 * @param {Object} [params={}] - Query parameters
 * @param {string} [params.servedBy] - Filter by staff ID
 * @param {string} [params.customerId] - Filter by customer ID
 * @param {string} [params.paymentStatus] - Filter by payment status
 * @param {string} [params.startDate] - Start date (ISO format)
 * @param {string} [params.endDate] - End date (ISO format)
 * @param {number} [params.page] - Page number
 * @param {number} [params.limit] - Page size
 * @returns {Promise<Object>} API response with sales and pagination
 */
export const listSales = async (params = {}) => {
  try {
    const response = await api.get('/cafe/sales', { params });
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Get a single sale by ID
 * 
 * @param {string} id - Sale ID
 * @returns {Promise<Object>} API response with sale data
 */
export const getSale = async (id) => {
  try {
    const response = await api.get(`/cafe/sales/${id}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Create a payment for a cafe sale (cash only)
 * 
 * @param {Object} data - Payment data
 * @param {string} data.cafeSaleId - Cafe sale ID
 * @param {string} data.workspace - Workspace ID
 * @param {string} [data.notes] - Payment notes
 * @returns {Promise<Object>} API response with payment and receipt
 * 
 * @example
 * const payment = await createCafePayment({
 *   cafeSaleId: 'sale123',
 *   workspace: 'workspace123'
 * });
 * console.log(payment.receiptNumber); // 'RCP-XXXXXXXXXX'
 */
export const createCafePayment = async (data) => {
  try {
    const response = await api.post('/payments/cafe', data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

// ===========================================
// EXPENSE API FUNCTIONS (T144)
// ===========================================

/**
 * List expenses with optional filters
 * 
 * @param {Object} [params={}] - Query parameters
 * @param {string} [params.expenseType] - Filter by expense type
 * @param {string} [params.startDate] - Start date (ISO format)
 * @param {string} [params.endDate] - End date (ISO format)
 * @param {number} [params.page] - Page number
 * @param {number} [params.limit] - Page size
 * @param {string} [params.sort] - Sort field:direction
 * @returns {Promise<Object>} API response with expenses and pagination
 */
export const listExpenses = async (params = {}) => {
  try {
    const response = await api.get('/cafe/expenses', { params });
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Get a single expense by ID
 * 
 * @param {string} id - Expense ID
 * @returns {Promise<Object>} API response with expense data
 */
export const getExpense = async (id) => {
  try {
    const response = await api.get(`/cafe/expenses/${id}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Create a new expense
 * 
 * @param {Object} data - Expense data
 * @param {string} data.description - Expense description
 * @param {number} data.amount - Expense amount
 * @param {string} data.expenseType - Expense type/category
 * @param {string} [data.vendor] - Vendor name
 * @param {string} [data.date] - Expense date (ISO format)
 * @param {string} [data.notes] - Additional notes
 * @returns {Promise<Object>} API response with created expense
 */
export const createExpense = async (data) => {
  try {
    const response = await api.post('/cafe/expenses', data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Update an existing expense
 * 
 * @param {string} id - Expense ID
 * @param {Object} data - Updated expense data
 * @returns {Promise<Object>} API response with updated expense
 */
export const updateExpense = async (id, data) => {
  try {
    const response = await api.patch(`/cafe/expenses/${id}`, data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Delete an expense
 * 
 * @param {string} id - Expense ID
 * @returns {Promise<Object>} API response
 */
export const deleteExpense = async (id) => {
  try {
    const response = await api.delete(`/cafe/expenses/${id}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

// ===========================================
// DEFAULT EXPORT
// ===========================================

export default {
  // Item functions
  listItems,
  getItem,
  createItem,
  updateItem,
  deleteItem,
  restockItem,
  getLowStockItems,
  // Sales functions
  createSale,
  listSales,
  getSale,
  createCafePayment,
  // Expense functions (T144)
  listExpenses,
  getExpense,
  createExpense,
  updateExpense,
  deleteExpense
};
